/**
 * Add new texts block - paste or type, add to list.
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FC } from 'react';
import { Plus } from 'lucide-react';

import { Button } from '@/components/ui';
import { parseSmsTextsInput } from '@/features/smswiser-campaigns/components/PrepareNewDropsForm';

export interface AddNewTextBlockProps {
  value: string;
  onChange: (v: string) => void;
  error: string | null;
  onAdd: () => void;
  onPasteAdd: (texts: string[]) => void;
  disabled: boolean;
}

export const AddNewTextBlock: FC<AddNewTextBlockProps> = ({
  value,
  onChange,
  error,
  onAdd,
  onPasteAdd,
  disabled,
}) => {
  const handlePaste = (e: React.ClipboardEvent) => {
    const pasted = e.clipboardData?.getData('text') ?? '';
    if (!pasted.trim()) return;
    if (pasted.trimStart().startsWith('[') || pasted.trimStart().startsWith('{')) {
      e.preventDefault();
      const { texts, error: err } = parseSmsTextsInput(pasted);
      if (!err && texts.length) onPasteAdd(texts);
    }
  };

  return (
    <div className="rounded-lg border border-border bg-muted/10 p-2">
      <textarea
        className="min-h-[72px] w-full resize-none rounded-md border bg-background p-2 text-xs outline-none"
        placeholder='Paste text or JSON (e.g. ["Text A","Text B"])'
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onPaste={handlePaste}
        disabled={disabled}
      />
      {error && (
        <p className="mt-1 text-xs text-destructive" role="alert">
          {error}
        </p>
      )}
      <div className="mt-2 flex justify-end">
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-7 gap-1 text-xs"
          onClick={onAdd}
          disabled={!value.trim() || disabled}
        >
          <Plus className="h-3 w-3" /> Add
        </Button>
      </div>
    </div>
  );
};
